const POSTCODE_LOOKUP_URL = process.env.REACT_APP_POSTCODE_API;

// console.log("Postcode Lookup URL:", POSTCODE_LOOKUP_URL);

function buildErrorMessage(data, fallback = "") {
  if (!data) return fallback;

  if (typeof data === "string") return data;

  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.details?.message === "string") return data.details.message;

  return fallback;
}

export async function getAddressAutocomplete(query, { signal } = {}) {
  try {
    if (!POSTCODE_LOOKUP_URL) {
      return { ok: false, error: "Server URL missing" };
    }

    const res = await fetch(`${POSTCODE_LOOKUP_URL}/api/experian/address/search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      signal,
      body: JSON.stringify({
        text: query,
        country_iso: "GBR",
        datasets: ["gb-address"],
        max_suggestions: 7,
      }),
    });

    const data = await res.json().catch(() => ({}));

    // console.log("Address autocomplete response:", { data, res });

    if (!res.ok) {
      return {
        ok: false,
        error: buildErrorMessage(data, "Failed to search addresses"),
      };
    }

    const suggestions = Array.isArray(data?.result?.suggestions)
      ? data.result.suggestions.map((item) => ({
          id: item?.global_address_key || "",
          address: item?.text || "",
          raw: item || {},
        }))
      : [];

    return {
      ok: true,
      suggestions,
      raw: data || {},
    };
  } catch (err) {
    if (err?.name === "AbortError") {
      return { ok: false, aborted: true };
    }

    return {
      ok: false,
      error:
        typeof err?.message === "string"
          ? err.message
          : "Address search failed",
    };
  }
}

export async function getAddressById(id, { signal } = {}) {
  try {
    if (!POSTCODE_LOOKUP_URL) {
      return { ok: false, error: "Server URL missing" };
    }

    const res = await fetch(
      `${POSTCODE_LOOKUP_URL}/api/experian/address/format/${encodeURIComponent(id)}?addComponents=true`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal,
      },
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      return {
        ok: false,
        error: buildErrorMessage(data, "Failed to fetch address"),
      };
    }

    const address = data?.result?.address || {};
    const components = data?.result?.components || {};

    const building = components?.building || {};
    const street = components?.street || {};
    const locality = components?.locality || {};
    const postalCode = components?.postal_code || {};

    const mappedAddress = {
      building_number: building?.building_number || "",
      building_name: building?.building_name || "",

      thoroughfare:
        street?.full_name ||
        [address?.address_line_2, address?.address_line_3]
          .filter(Boolean)
          .join(" ") ||
        address?.address_line_1 ||
        "",

      line_1: address?.address_line_1 || "",
      line_2: address?.address_line_2 || "",
      line_3: address?.address_line_3 || "",

      town: locality?.town?.name || address?.locality || "",
      town_or_city: locality?.town?.name || address?.locality || "",

      county:
        locality?.sub_region?.name ||
        locality?.region?.name ||
        address?.region ||
        "",

      postcode:
        postalCode?.full_name ||
        postalCode?.primary ||
        address?.postal_code ||
        "",

      country: components?.country_name || address?.country || "",
    };

    return {
      ok: true,
      address: mappedAddress,
      raw: data || {},
    };
  } catch (err) {
    if (err?.name === "AbortError") {
      return { ok: false, aborted: true };
    }

    return {
      ok: false,
      error:
        typeof err?.message === "string"
          ? err.message
          : "Failed to fetch address",
    };
  }
}
